import { createPortal } from 'react-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/apiClient';
import Modal from '../ui/Modal';
import Breadcrumb from '../ui/Breadcrumb';
import ModuleIcon from '../ui/ModuleIcon';
import { IconEdit, IconPlus, IconTrash } from '../ui/icons';
import { moduleColor, orderModules } from '../ui/modulePalette';

function newId(prefix) {
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function safeJsonParse(input, fallback) {
  try {
    const v = JSON.parse(String(input || ''));
    return v ?? fallback;
  } catch {
    return fallback;
  }
}

function ts(input) {
  const t = Date.parse(String(input || ''));
  return Number.isFinite(t) ? t : 0;
}

function compactCount(n) {
  const v = Number(n || 0);
  if (!Number.isFinite(v) || v <= 0) return '0';
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(v);
}

function formatDateTimeFr(input) {
  const t = Date.parse(String(input || ''));
  if (!Number.isFinite(t)) return '';
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(t));
  } catch {
    return new Date(t).toLocaleString('fr-FR');
  }
}

function menuFormulaDisplayName(formula) {
  const f = formula && typeof formula === 'object' ? formula : {};
  const title = String(f.title || '').trim();
  const subtitle = String(f.subtitle || '').trim();
  if (title) return title;
  if (subtitle) return subtitle;
  const id = Number(f.id || 0);
  return id > 0 ? `Formule #${id}` : 'Formule';
}

function dayKey(input) {
  const d = new Date(input || Date.now());
  d.setHours(0, 0, 0, 0);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function buildTrend(rows, getDate, days = 7) {
  const out = [];
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const counts = new Map();
  for (const row of rows || []) {
    const k = dayKey(getDate(row));
    counts.set(k, (counts.get(k) || 0) + 1);
  }
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = new Date(now);
    d.setDate(now.getDate() - i);
    const k = dayKey(d);
    out.push(counts.get(k) || 0);
  }
  return out;
}

const SIZE_CHOICES = [
  { key: 'sm', label: 'Petit carré' },
  { key: 'md', label: 'Rectangle' },
  { key: 'lg', label: 'Grand carré' },
];

function normalizeSize(raw) {
  const s = String(raw || '').trim();
  if (s === 'sm' || s === 'md' || s === 'lg') return s;
  return 'md';
}

function normalizeWidget(raw, index = 0) {
  const src = raw && typeof raw === 'object' ? raw : {};
  const id = String(src.id || '') || `w_${index}`;
  const kind = String(src.kind || src.type || '').trim();
  const size = normalizeSize(src.size);

  if (kind === 'module') {
    return {
      id,
      kind,
      module_key: String(src.module_key || ''),
      title: String(src.title || ''),
      size,
      project_items: Array.isArray(src.project_items) ? src.project_items : [],
    };
  }

  if (kind === 'shortcut') {
    return {
      id,
      kind,
      module_key: String(src.module_key || ''),
      action_key: String(src.action_key || ''),
      title: String(src.title || ''),
      size,
      project_id: Number(src.project_id || 0),
      script_id: Number(src.script_id || 0),
      formula_id: Number(src.formula_id || 0),
    };
  }

  if (kind === 'stats') {
    return {
      id,
      kind,
      module_key: String(src.module_key || ''),
      metric_key: String(src.metric_key || ''),
      page_path: String(src.page_path || ''),
      title: String(src.title || ''),
      size,
    };
  }

  if (kind === 'graph') {
    return {
      id,
      kind,
      module_key: String(src.module_key || ''),
      metric_key: String(src.metric_key || ''),
      title: String(src.title || ''),
      size,
    };
  }

  if (kind === 'link') {
    return {
      id,
      kind,
      route: String(src.route || ''),
      title: String(src.title || ''),
      size,
    };
  }

  if (String(src.type || '') === 'project') {
    const pid = Number(src.project_id || 0);
    return {
      id,
      kind: 'shortcut',
      module_key: 'projects',
      action_key: 'projects_open_id',
      project_id: Number.isFinite(pid) && pid > 0 ? pid : 0,
      title: String(src.title || ''),
      size,
    };
  }

  return null;
}

function moduleTemplates(moduleKey, moduleLabel, { menuFormulas = [] } = {}) {
  const actions = [
    {
      key: `${moduleKey}_open`,
      kind: 'module',
      module_key: moduleKey,
      title: moduleLabel,
    },
  ];
  const infos = [];
  const graphs = [];

  if (moduleKey === 'projects') {
    actions.push(
      {
        key: 'projects_last',
        kind: 'shortcut',
        module_key: 'projects',
        action_key: 'projects_last',
        title: 'Modifier le dernier projet',
      },
      {
        key: 'projects_open_id',
        kind: 'shortcut',
        module_key: 'projects',
        action_key: 'projects_open_id',
        title: 'Modifier un projet précis',
      }
    );
    infos.push({
      key: 'projects_overview',
      kind: 'stats',
      module_key: 'projects',
      metric_key: 'projects_overview',
      title: 'Infos Projets',
    });
    graphs.push({
      key: 'projects_trend',
      kind: 'graph',
      module_key: 'projects',
      metric_key: 'projects_trend',
      title: 'Activité Projets',
    });
  }

  if (moduleKey === 'menus') {
    const formulas = Array.isArray(menuFormulas) ? menuFormulas : [];
    for (const formula of formulas) {
      const fid = Number(formula?.id || 0);
      if (!Number.isFinite(fid) || fid <= 0) continue;
      const label = menuFormulaDisplayName(formula);
      actions.push({
        key: `menus_formula_${fid}`,
        kind: 'shortcut',
        module_key: 'menus',
        action_key: 'menus_formula_edit',
        formula_id: fid,
        title: `Modifier: ${label}`,
      });
    }
    infos.push({
      key: 'menus_overview',
      kind: 'stats',
      module_key: 'menus',
      metric_key: 'menus_overview',
      title: 'Infos Menus',
    });
  }

  if (moduleKey === 'updates') {
    actions.push({
      key: 'updates_new',
      kind: 'shortcut',
      module_key: 'updates',
      action_key: 'updates_new',
      title: 'Nouvelle mise à jour',
    });
    infos.push({
      key: 'updates_overview',
      kind: 'stats',
      module_key: 'updates',
      metric_key: 'updates_overview',
      title: 'Infos Mises à jour',
    });
    graphs.push({
      key: 'updates_trend',
      kind: 'graph',
      module_key: 'updates',
      metric_key: 'updates_trend',
      title: 'Activité Mises à jour',
    });
  }

  if (moduleKey === 'traffic') {
    infos.push(
      {
        key: 'traffic_overview',
        kind: 'stats',
        module_key: 'traffic',
        metric_key: 'traffic_overview',
        title: 'Visites (30j)',
      },
      {
        key: 'traffic_page_views',
        kind: 'stats',
        module_key: 'traffic',
        metric_key: 'traffic_page_views',
        page_path: '',
        title: 'Vues d’une page',
      }
    );
    graphs.push({
      key: 'traffic_top_pages',
      kind: 'graph',
      module_key: 'traffic',
      metric_key: 'traffic_top_pages',
      title: 'Top pages',
    });
  }

  if (moduleKey === 'appointments') {
    infos.push({
      key: 'appointments_next',
      kind: 'stats',
      module_key: 'appointments',
      metric_key: 'appointments_next',
      title: 'Prochain rendez-vous',
    });
  }

  if (moduleKey === 'databases') {
    actions.push({
      key: 'databases_check',
      kind: 'shortcut',
      module_key: 'databases',
      action_key: 'databases_check',
      title: 'Vérifier la connexion',
    });
    infos.push({
      key: 'databases_overview',
      kind: 'stats',
      module_key: 'databases',
      metric_key: 'databases_overview',
      title: 'État BDD',
    });
  }

  if (moduleKey === 'scripts') {
    actions.push(
      {
        key: 'scripts_run_last',
        kind: 'shortcut',
        module_key: 'scripts',
        action_key: 'scripts_run_last',
        title: 'Exécuter le dernier script',
      },
      {
        key: 'scripts_run_id',
        kind: 'shortcut',
        module_key: 'scripts',
        action_key: 'scripts_run_id',
        title: 'Exécuter un script',
      }
    );
    infos.push({
      key: 'scripts_overview',
      kind: 'stats',
      module_key: 'scripts',
      metric_key: 'scripts_overview',
      title: 'Scripts',
    });
  }

  return { actions, infos, graphs };
}

function resolveWidgetRoute(widget, moduleRoute, insights) {
  if (!widget) return '';
  if (widget.kind === 'link') return String(widget.route || '').trim();
  if (widget.kind !== 'shortcut') return moduleRoute;

  const action = String(widget.action_key || '');
  if (action === 'projects_last') {
    const id = Number(insights?.projects?.latest?.id || 0);
    return id > 0 ? `/projects/${id}` : '/projects';
  }
  if (action === 'projects_open_id') {
    const id = Number(widget.project_id || 0);
    return id > 0 ? `/projects/${id}` : '/projects';
  }
  return moduleRoute;
}

function defaultWidgetTitle(widget, moduleLabel = '') {
  const kind = String(widget?.kind || '');
  const moduleName = String(moduleLabel || '').trim();
  if (kind === 'module') return moduleName || 'Module';
  if (kind === 'shortcut') return String(widget?.title || moduleName || 'Raccourci');
  if (kind === 'stats') return String(widget?.title || `Infos ${moduleName || ''}`).trim();
  if (kind === 'graph') return String(widget?.title || `Graphique ${moduleName || ''}`).trim();
  if (kind === 'link') return String(widget?.title || widget?.route || 'Lien');
  return 'Widget';
}

function SparkBars({ values, accent }) {
  const list = Array.isArray(values) ? values : [];
  const max = Math.max(1, ...list.map((x) => Number(x) || 0));
  if (!list.length) return <div className="Muted">Pas de données</div>;
  return (
    <div className="WidgetBars" aria-hidden="true">
      {list.map((v, i) => {
        const n = Number(v || 0);
        const h = Math.max(8, Math.round((n / max) * 100));
        return <span key={`bar_${i}`} className="WidgetBar" style={{ height: `${h}%`, '--bar-accent': accent }} title={`${n}`} />;
      })}
    </div>
  );
}

function DashboardWidget({ widget, insights, onNavigate, resizing, onWidgetClick, runtimeHint }) {
  const size = normalizeSize(widget.size);
  const accent = String(widget.color || 'var(--primary)');
  const canOpen = String(widget.route || '').startsWith('/');
  const iconKey = String(widget.iconKey || widget.module_key || 'dashboard');

  let metric = '';
  let detail = '';
  let trend = [];

  if (widget.kind === 'module') {
    if (widget.module_key === 'projects') {
      const total = Number(insights?.projects?.count || 0);
      metric = `${compactCount(total)} projet${total > 1 ? 's' : ''}`;
      detail = insights?.projects?.latest?.name ? `Dernier: ${insights.projects.latest.name}` : 'Aucun projet';
      trend = insights?.projects?.trend || [];
    } else if (widget.module_key === 'updates') {
      const total = Number(insights?.updates?.count || 0);
      metric = `${compactCount(total)} mise${total > 1 ? 's' : ''} à jour`;
      detail = insights?.updates?.latest?.title ? `Dernière: ${insights.updates.latest.title}` : 'Aucune mise à jour';
      trend = insights?.updates?.trend || [];
    } else if (widget.module_key === 'menus') {
      const cats = Number(insights?.menus?.categories || 0);
      const formulas = Number(insights?.menus?.formulas || 0);
      metric = `${cats} catégorie${cats > 1 ? 's' : ''}`;
      detail = `${formulas} formule${formulas > 1 ? 's' : ''}`;
    } else if (widget.module_key === 'traffic') {
      const v = insights?.traffic?.summary?.views_30d;
      metric = v == null ? '—' : `${compactCount(v)} visites`;
      detail = '30 derniers jours';
      trend = (insights?.traffic?.topPages || []).slice(0, 7).map((x) => Number(x?.views || 0));
    } else if (widget.module_key === 'appointments') {
      const nextAt = String(insights?.appointments?.next?.start_at || '').trim();
      metric = nextAt ? 'Prochain RDV' : 'Aucun RDV';
      detail = nextAt ? formatDateTimeFr(nextAt) : 'Pas de rendez-vous planifié';
    } else if (widget.module_key === 'databases') {
      const configured = Number(insights?.databases?.configured || 0);
      metric = configured > 0 ? 'BDD configurée' : 'BDD absente';
      detail = configured > 0 ? 'Cliquer pour vérifier' : 'Configurer la connexion';
    } else if (widget.module_key === 'scripts') {
      const count = Number(insights?.scripts?.count || 0);
      metric = `${compactCount(count)} script${count > 1 ? 's' : ''}`;
      detail = count > 0 ? 'Cliquer pour exécuter' : 'Aucun script';
    } else {
      metric = 'Ouvrir le module';
      detail = 'Raccourci rapide';
    }
  } else if (widget.kind === 'shortcut') {
    metric = 'Raccourci';
    if (widget.action_key === 'menus_formula_edit') {
      const formulas = Array.isArray(insights?.menus?.formulas_list) ? insights.menus.formulas_list : [];
      const fid = Number(widget.formula_id || 0);
      const found = formulas.find((f) => Number(f?.id || 0) === fid) || null;
      detail = found ? menuFormulaDisplayName(found) : 'Choisis une formule';
    } else if (widget.action_key === 'menus_daily') detail = 'Carte du jour';
    else if (widget.action_key === 'menus_weekly') detail = 'Carte de la semaine';
    else if (widget.action_key === 'projects_last') detail = insights?.projects?.latest?.name || 'Dernier projet';
    else if (widget.action_key === 'projects_open_id') detail = widget.project_id ? `Projet #${widget.project_id}` : 'Choisis un projet';
    else if (widget.action_key === 'updates_new') detail = 'Créer une mise à jour';
    else if (widget.action_key === 'databases_check') detail = 'Tester la connexion BDD';
    else if (widget.action_key === 'scripts_run_last') detail = insights?.scripts?.latest?.name || 'Dernier script';
    else if (widget.action_key === 'scripts_run_id') detail = widget.script_id ? `Script #${widget.script_id}` : 'Choisis un script';
    else detail = 'Ouvrir';
  } else if (widget.kind === 'stats') {
    if (widget.metric_key === 'projects_overview') {
      const total = Number(insights?.projects?.count || 0);
      metric = compactCount(total);
      detail = `projet${total > 1 ? 's' : ''}`;
    } else if (widget.metric_key === 'updates_overview') {
      const major = Number(insights?.updates?.major || 0);
      const minor = Number(insights?.updates?.minor || 0);
      metric = `${major}/${minor}`;
      detail = 'majeur / mineur';
    } else if (widget.metric_key === 'menus_overview') {
      const cats = Number(insights?.menus?.categories || 0);
      metric = compactCount(cats);
      detail = 'catégories';
    } else if (widget.metric_key === 'traffic_overview') {
      const v = insights?.traffic?.summary?.views_30d;
      metric = v == null ? '—' : compactCount(v);
      detail = 'visites (30j)';
    } else if (widget.metric_key === 'traffic_page_views') {
      const path = String(widget.page_path || '').trim();
      const topPages = Array.isArray(insights?.traffic?.topPages) ? insights.traffic.topPages : [];
      const exact = path ? topPages.find((p) => String(p.path || '') === path) : null;
      metric = exact ? compactCount(exact.views) : path ? '0' : '—';
      detail = path ? path : 'Choisis une page en propriétés';
    } else if (widget.metric_key === 'appointments_next') {
      const nextAt = String(insights?.appointments?.next?.start_at || '').trim();
      metric = nextAt ? formatDateTimeFr(nextAt) : '—';
      detail = nextAt ? 'prochain rendez-vous' : 'Aucun rendez-vous planifié';
    } else if (widget.metric_key === 'databases_overview') {
      const configured = Number(insights?.databases?.configured || 0);
      metric = configured > 0 ? 'OK' : '—';
      detail = configured > 0 ? 'Connexion configurée' : 'Non configurée';
    } else if (widget.metric_key === 'scripts_overview') {
      const count = Number(insights?.scripts?.count || 0);
      metric = compactCount(count);
      detail = `script${count > 1 ? 's' : ''}`;
    } else {
      metric = '—';
      detail = 'Aucune donnée';
    }
  } else if (widget.kind === 'graph') {
    if (widget.metric_key === 'projects_trend') {
      metric = 'Activité projets';
      detail = '7 derniers jours';
      trend = insights?.projects?.trend || [];
    } else if (widget.metric_key === 'updates_trend') {
      metric = 'Activité mises à jour';
      detail = '7 derniers jours';
      trend = insights?.updates?.trend || [];
    } else if (widget.metric_key === 'traffic_top_pages') {
      metric = 'Top pages';
      detail = '30 derniers jours';
      trend = (insights?.traffic?.topPages || []).slice(0, 7).map((x) => Number(x?.views || 0));
    }
  } else if (widget.kind === 'link') {
    metric = 'Lien personnalisé';
    detail = String(widget.route || '');
  }

  if (runtimeHint && typeof runtimeHint === 'object') {
    if (String(runtimeHint.metric || '').trim()) metric = String(runtimeHint.metric);
    if (String(runtimeHint.detail || '').trim()) detail = String(runtimeHint.detail);
  }

  const body = (
    <>
      <div className="DashboardWidgetHead">
        <span className="DashboardWidgetIcon" aria-hidden="true">
          <ModuleIcon moduleKey={iconKey} />
        </span>
        <span className="DashboardWidgetTitle OneLine">{widget.title}</span>
      </div>

      <div className="DashboardWidgetBody">
        <div className="DashboardWidgetMetric">{metric || '—'}</div>
        {size !== 'sm' ? <div className="DashboardWidgetDetail OneLine">{detail || ' '}</div> : null}
        {(widget.kind === 'graph' || (widget.kind === 'module' && size === 'lg' && (widget.module_key === 'projects' || widget.module_key === 'updates' || widget.module_key === 'traffic'))) && size !== 'sm' ? (
          <SparkBars values={trend} accent={accent} />
        ) : null}
      </div>
    </>
  );

  const cls = `DashboardWidget DashboardWidget--size-${size} ${resizing ? 'is-resizing' : ''}`;
  const isClickable = Boolean(canOpen || onWidgetClick);
  if (!isClickable) {
    return (
      <div className={cls} style={{ '--accent': accent }}>
        {body}
      </div>
    );
  }
  return (
    <button
      type="button"
      className={cls}
      style={{ '--accent': accent }}
      onClick={() => {
        if (onWidgetClick) {
          onWidgetClick(widget, { canOpen, onNavigate });
          return;
        }
        onNavigate?.(widget.route);
      }}
      title={widget.route}
    >
      {body}
    </button>
  );
}

function WidgetContextMenu({ menu, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!menu) return;
    function onKey(e) {
      if (e.key === 'Escape') onClose?.();
    }
    function onDown(e) {
      const el = ref.current;
      if (!el) return;
      if (el.contains(e.target)) return;
      onClose?.();
    }
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onClose, true);
    window.addEventListener('resize', onClose);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onClose, true);
      window.removeEventListener('resize', onClose);
    };
  }, [menu, onClose]);

  if (!menu || typeof document === 'undefined') return null;
  return createPortal(
    <div ref={ref} className="Menu Menu--fixed" style={{ position: 'fixed', left: menu.x, top: menu.y }} role="menu">
      {(Array.isArray(menu.items) ? menu.items : []).map((it, idx) => {
        if (it?.type === 'sep') return <div key={`sep_${idx}`} className="MenuSeparator" role="separator" />;
        return (
          <button
            key={it.key || `it_${idx}`}
            type="button"
            className={`MenuItem ${it.variant === 'danger' ? 'MenuItem--danger' : ''}`}
            role="menuitem"
            disabled={Boolean(it.disabled)}
            onClick={() => {
              if (it.disabled) return;
              onClose?.();
              it.onClick?.();
            }}
          >
            {it.label}
          </button>
        );
      })}
    </div>,
    document.body
  );
}

function requiresMandatoryProperties(widget) {
  const w = widget && typeof widget === 'object' ? widget : null;
  if (!w) return false;
  if (String(w.kind) === 'stats' && String(w.metric_key) === 'traffic_page_views') return true;
  if (String(w.kind) === 'shortcut' && String(w.module_key) === 'projects' && String(w.action_key) === 'projects_open_id') return true;
  if (String(w.kind) === 'shortcut' && String(w.module_key) === 'scripts' && String(w.action_key) === 'scripts_run_id') return true;
  if (String(w.kind) === 'shortcut' && String(w.module_key) === 'menus' && String(w.action_key) === 'menus_formula_edit') return true;
  return false;
}

function validateWidgetRequiredFields(widgetLike) {
  const w = widgetLike && typeof widgetLike === 'object' ? widgetLike : {};
  if (String(w.kind) === 'stats' && String(w.metric_key) === 'traffic_page_views') {
    if (!String(w.page_path || '').trim()) return 'Choisis une page suivie.';
  }
  if (String(w.kind) === 'shortcut' && String(w.module_key) === 'projects' && String(w.action_key) === 'projects_open_id') {
    if (!Number.isFinite(Number(w.project_id)) || Number(w.project_id) <= 0) return 'Choisis un projet.';
  }
  if (String(w.kind) === 'shortcut' && String(w.module_key) === 'scripts' && String(w.action_key) === 'scripts_run_id') {
    if (!Number.isFinite(Number(w.script_id)) || Number(w.script_id) <= 0) return 'Choisis un script.';
  }
  if (String(w.kind) === 'shortcut' && String(w.module_key) === 'menus' && String(w.action_key) === 'menus_formula_edit') {
    if (!Number.isFinite(Number(w.formula_id)) || Number(w.formula_id) <= 0) return 'Choisis une formule.';
  }
  return '';
}

function widgetFormFromValue(widget) {
  const w = widget && typeof widget === 'object' ? widget : {};
  return {
    title: String(w.title || ''),
    size: normalizeSize(w.size),
    page_path: String(w.page_path || ''),
    action_key: String(w.action_key || ''),
    project_id: Number(w.project_id || 0),
    script_id: Number(w.script_id || 0),
    formula_id: Number(w.formula_id || 0),
  };
}

function safeMenuDataForDashboard(raw) {
  const src = raw && typeof raw === 'object' ? raw : {};
  const categories = Array.isArray(src?.permanent?.categories) ? src.permanent.categories : [];
  const formulas = Array.isArray(src?.formulas) ? src.formulas : [];
  return {
    version: 1,
    permanent: { categories },
    formulas: formulas.map((f) => ({
      ...(f && typeof f === 'object' ? f : {}),
      id: String(f?.id || '') || `mf_${Date.now()}_${Math.random().toString(16).slice(2, 8)}`,
      title: String(f?.title || ''),
      subtitle: String(f?.subtitle || ''),
      description: String(f?.description || ''),
      price: String(f?.price || ''),
      media_id: f?.media_id == null ? null : Number(f.media_id) || null,
      media_name: String(f?.media_name || ''),
      image_url: String(f?.image_url || ''),
      is_available: f?.is_available !== false,
    })),
  };
}

export default function DashboardPage({ user, siteId, modules, onNavigate }) {
  const storageKey = useMemo(() => `omniscope:dashboardWidgets:${String(user?.id || 'anon')}:${String(siteId || '')}`, [user?.id, siteId]);

  const [widgets, setWidgets] = useState([]);
  const [hydrated, setHydrated] = useState(false);
  const [modal, setModal] = useState(null);
  const [menu, setMenu] = useState(null);
  const [dragWidgetId, setDragWidgetId] = useState('');
  const [dragTarget, setDragTarget] = useState({ id: '', side: 'before' });
  const [resizingWidgetId, setResizingWidgetId] = useState('');
  const [runtimeByWidgetId, setRuntimeByWidgetId] = useState({});
  const resizeTimerRef = useRef(null);

  const [insights, setInsights] = useState({
    projects: { count: 0, latest: null, trend: [], list: [] },
    updates: { count: 0, major: 0, minor: 0, latest: null, trend: [] },
    menus: { categories: 0, formulas: 0, formulas_list: [] },
    traffic: { summary: { views_30d: null }, topPages: [] },
    appointments: { count: 0, next: null },
    databases: { configured: 0 },
    scripts: { count: 0, latest: null, list: [] },
  });

  useEffect(() => {
    return () => {
      if (resizeTimerRef.current) window.clearTimeout(resizeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    setHydrated(false);
    if (!siteId) {
      setWidgets([]);
      setHydrated(true);
      return;
    }
    const raw = window.localStorage.getItem(storageKey);
    const list = safeJsonParse(raw, []);
    const normalized = (Array.isArray(list) ? list : []).map((w, i) => normalizeWidget(w, i)).filter(Boolean);
    setWidgets(normalized);
    setHydrated(true);
  }, [storageKey, siteId]);

  useEffect(() => {
    if (!hydrated || !siteId) return;
    window.localStorage.setItem(storageKey, JSON.stringify(widgets));
  }, [storageKey, widgets, hydrated, siteId]);

  const availableModules = useMemo(() => {
    const list = (modules || []).filter((m) => m.route && m.key && m.key !== 'dashboard' && !m.coming_soon);
    return orderModules(list);
  }, [modules]);

  const availableByKey = useMemo(() => {
    const m = new Map();
    for (const mod of availableModules || []) m.set(String(mod.key), mod);
    return m;
  }, [availableModules]);

  useEffect(() => {
    let cancelled = false;
    if (!siteId) {
      setInsights({
        projects: { count: 0, latest: null, trend: [], list: [] },
        updates: { count: 0, major: 0, minor: 0, latest: null, trend: [] },
        menus: { categories: 0, formulas: 0, formulas_list: [] },
        traffic: { summary: { views_30d: null }, topPages: [] },
        appointments: { count: 0, next: null },
        databases: { configured: 0 },
        scripts: { count: 0, latest: null, list: [] },
      });
      return;
    }

    async function run() {
      const next = {
        projects: { count: 0, latest: null, trend: [], list: [] },
        updates: { count: 0, major: 0, minor: 0, latest: null, trend: [] },
        menus: { categories: 0, formulas: 0, formulas_list: [] },
        traffic: { summary: { views_30d: null }, topPages: [] },
        appointments: { count: 0, next: null },
        databases: { configured: 0 },
        scripts: { count: 0, latest: null, list: [] },
      };

      const tasks = [];

      if (availableByKey.has('projects')) {
        tasks.push(
          api.projectsList(Number(siteId), {}).then((res) => {
            if (!res?.ok) return;
            const list = Array.isArray(res.projects) ? res.projects : [];
            next.projects.list = list.map((p) => ({ id: Number(p.id || 0), name: String(p.name || '') }));
            next.projects.count = list.length;
            const sorted = [...list].sort((a, b) => ts(b?.updated_at || b?.created_at) - ts(a?.updated_at || a?.created_at));
            const latest = sorted[0] || null;
            next.projects.latest = latest ? { id: Number(latest.id || 0), name: String(latest.name || '') } : null;
            next.projects.trend = buildTrend(list, (x) => x?.updated_at || x?.created_at, 7);
          })
        );
      }

      if (availableByKey.has('updates')) {
        tasks.push(
          api.updatesList(Number(siteId), { limit: 100 }).then((res) => {
            if (!res?.ok) return;
            const list = Array.isArray(res.updates) ? res.updates : [];
            next.updates.count = list.length;
            next.updates.major = list.filter((x) => String(x?.level || '') === 'major').length;
            next.updates.minor = list.filter((x) => String(x?.level || '') !== 'major').length;
            const sorted = [...list].sort((a, b) => ts(b?.created_at) - ts(a?.created_at));
            const latest = sorted[0] || null;
            next.updates.latest = latest ? { id: Number(latest.id || 0), title: String(latest.title || '') } : null;
            next.updates.trend = buildTrend(list, (x) => x?.created_at, 7);
          })
        );
      }

      if (availableByKey.has('menus')) {
        tasks.push(
          api.menusGet(Number(siteId)).then((res) => {
            if (!res?.ok) return;
            const menuObj = res.menu && typeof res.menu === 'object' ? res.menu : {};
            const categories = Array.isArray(menuObj?.permanent?.categories) ? menuObj.permanent.categories : [];
            const formulas = Array.isArray(menuObj?.formulas) ? menuObj.formulas : [];
            next.menus.categories = categories.length;
            next.menus.formulas = formulas.length;
            next.menus.formulas_list = formulas
              .map((f) => ({
                id: Number(f?.id || 0),
                title: String(f?.title || ''),
                subtitle: String(f?.subtitle || ''),
                description: String(f?.description || ''),
                price: String(f?.price || ''),
                is_available: f?.is_available !== false,
              }))
              .filter((f) => Number.isFinite(f.id) && f.id > 0);
          })
        );
      }

      if (availableByKey.has('traffic')) {
        tasks.push(
          api.traffic(Number(siteId)).then((res) => {
            if (!res?.ok) return;
            next.traffic.summary = {
              views_30d: res?.summary?.views_30d ?? null,
            };
            next.traffic.topPages = Array.isArray(res?.top_pages_30d)
              ? res.top_pages_30d
                  .map((p) => ({ path: String(p?.path || ''), views: Number(p?.views || 0) }))
                  .filter((p) => p.path)
              : [];
          })
        );
      }

      if (availableByKey.has('appointments')) {
        tasks.push(
          api.appointmentsList(Number(siteId), { limit: 300 }).then((res) => {
            if (!res?.ok) return;
            const list = Array.isArray(res.appointments) ? res.appointments : [];
            next.appointments.count = list.length;
            const now = Date.now();
            const valid = list.filter((a) => String(a?.status || '') !== 'cancelled');
            const withDate = valid
              .map((a) => ({ ...a, _t: ts(a?.start_at) }))
              .filter((a) => a._t > 0)
              .sort((a, b) => a._t - b._t);
            const upcoming = withDate.find((a) => a._t >= now) || null;
            next.appointments.next = upcoming
              ? {
                  id: Number(upcoming.id || 0),
                  start_at: String(upcoming.start_at || ''),
                  client_name: String(upcoming.client_name || ''),
                  status: String(upcoming.status || ''),
                }
              : null;
          })
        );
      }

      if (availableByKey.has('databases')) {
        tasks.push(
          api.dbConnections(Number(siteId)).then((res) => {
            if (!res?.ok) return;
            const list = Array.isArray(res.connections) ? res.connections : [];
            next.databases.configured = list.length;
          })
        );
      }

      if (availableByKey.has('scripts')) {
        tasks.push(
          api.scriptsList(Number(siteId), { limit: 100 }).then((res) => {
            if (!res?.ok) return;
            const list = Array.isArray(res.scripts) ? res.scripts : [];
            next.scripts.list = list.map((s) => ({ id: Number(s.id || 0), name: String(s.name || '') }));
            next.scripts.count = list.length;
            const latest = [...list].sort((a, b) => ts(b?.updated_at || b?.created_at) - ts(a?.updated_at || a?.created_at))[0] || null;
            next.scripts.latest = latest ? { id: Number(latest.id || 0), name: String(latest.name || '') } : null;
          })
        );
      }

      await Promise.all(tasks);
      if (cancelled) return;
      setInsights(next);
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [siteId, availableByKey]);

  function setRuntimeHint(widgetId, hint) {
    const wid = String(widgetId || '');
    if (!wid) return;
    setRuntimeByWidgetId((prev) => ({ ...(prev || {}), [wid]: hint || null }));
    window.setTimeout(() => {
      setRuntimeByWidgetId((prev) => {
        if (!prev || !Object.prototype.hasOwnProperty.call(prev, wid)) return prev;
        const next = { ...(prev || {}) };
        delete next[wid];
        return next;
      });
    }, 4500);
  }

  async function runDatabasesCheck(widget) {
    setRuntimeHint(widget.id, { metric: 'Vérification…', detail: 'Test en cours' });
    const res = await api.dbConnectionTest({ site_id: Number(siteId), use_stored: true });
    if (!res?.ok) {
      setRuntimeHint(widget.id, { metric: 'Erreur', detail: String(res?.error || 'Connexion impossible') });
      return;
    }
    const dbName = String(res?.db?.name || '').trim();
    setRuntimeHint(widget.id, { metric: 'Connexion OK', detail: dbName ? dbName : 'Base disponible' });
  }

  async function runScriptFromWidget(widget) {
    const action = String(widget?.action_key || '');
    const lastId = Number(insights?.scripts?.latest?.id || 0);
    const explicitId = Number(widget?.script_id || 0);
    const scriptId = action === 'scripts_run_id' ? explicitId : lastId;
    if (!Number.isFinite(scriptId) || scriptId <= 0) {
      setRuntimeHint(widget.id, { metric: 'Aucun script', detail: 'Choisis un script dans Propriétés' });
      return;
    }
    setRuntimeHint(widget.id, { metric: 'Exécution…', detail: `Script #${scriptId}` });
    const res = await api.scriptsRun({ site_id: Number(siteId), id: scriptId });
    if (!res?.ok) {
      setRuntimeHint(widget.id, { metric: 'Erreur', detail: String(res?.error || 'Exécution impossible') });
      return;
    }
    setRuntimeHint(widget.id, { metric: 'Exécuté', detail: `Script #${scriptId}` });
  }

  function openQuickMenuActionModal(widget) {
    const action = String(widget?.action_key || '');
    const formulas = Array.isArray(insights?.menus?.formulas_list) ? insights.menus.formulas_list : [];
    const fid = Number(widget?.formula_id || 0);
    let selected = null;
    if (action === 'menus_formula_edit' && Number.isFinite(fid) && fid > 0) {
      selected = formulas.find((f) => Number(f?.id || 0) === fid) || null;
    } else if (action === 'menus_daily') {
      selected =
        formulas.find((f) => String(f?.subtitle || '').trim().toLowerCase() === 'carte du jour') ||
        formulas[0] ||
        null;
    } else if (action === 'menus_weekly') {
      selected =
        formulas.find((f) => String(f?.subtitle || '').trim().toLowerCase() === 'carte de la semaine') ||
        formulas[0] ||
        null;
    }
    if (!selected) {
      setRuntimeHint(widget?.id, { metric: 'Aucune formule', detail: 'Crée une formule dans Menus' });
      return;
    }
    const subtitle = String(selected.subtitle || '').trim();
    const title = String(selected.title || '').trim();
    setModal({
      type: 'quick-menu-action',
      widgetId: String(widget?.id || ''),
      formula_id: Number(selected.id || 0),
      formula_label: menuFormulaDisplayName(selected),
      title,
      subtitle,
      description: String(selected.description || ''),
      price: String(selected.price || ''),
      saving: false,
      error: '',
    });
  }

  function openQuickUpdateModal(widget) {
    setModal({
      type: 'quick-update',
      widgetId: String(widget?.id || ''),
      title: '',
      level: 'minor',
      changes_text: '',
      send_email: false,
      saving: false,
      error: '',
    });
  }

  function mergeUpdateIntoInsights(createdUpdate) {
    const row = createdUpdate && typeof createdUpdate === 'object' ? createdUpdate : null;
    if (!row) return;
    setInsights((prev) => {
      const cur = prev && typeof prev === 'object' ? prev : {};
      const upd = cur.updates && typeof cur.updates === 'object' ? cur.updates : {};
      const nextCount = Number(upd.count || 0) + 1;
      const level = String(row.level || 'minor');
      const nextMajor = Number(upd.major || 0) + (level === 'major' ? 1 : 0);
      const nextMinor = Number(upd.minor || 0) + (level === 'major' ? 0 : 1);
      return {
        ...cur,
        updates: {
          ...upd,
          count: nextCount,
          major: nextMajor,
          minor: nextMinor,
          latest: { id: Number(row.id || 0), title: String(row.title || '') },
          trend: Array.isArray(upd.trend) && upd.trend.length
            ? [...upd.trend.slice(0, Math.max(0, upd.trend.length - 1)), Number((upd.trend[upd.trend.length - 1] || 0) + 1)]
            : upd.trend,
        },
      };
    });
  }

  function handleWidgetClick(widget, { canOpen }) {
    const w = widget || null;
    if (!w) return;
    if (String(w.kind || '') === 'module' && String(w.module_key || '') === 'databases') {
      runDatabasesCheck(w);
      return;
    }
    if (String(w.kind || '') === 'shortcut' && String(w.module_key || '') === 'databases' && String(w.action_key || '') === 'databases_check') {
      runDatabasesCheck(w);
      return;
    }
    if (String(w.kind || '') === 'module' && String(w.module_key || '') === 'scripts') {
      runScriptFromWidget({ ...w, action_key: 'scripts_run_last' });
      return;
    }
    if (String(w.kind || '') === 'shortcut' && String(w.module_key || '') === 'scripts' && (String(w.action_key || '') === 'scripts_run_last' || String(w.action_key || '') === 'scripts_run_id')) {
      runScriptFromWidget(w);
      return;
    }
    if (String(w.kind || '') === 'shortcut' && String(w.module_key || '') === 'updates' && String(w.action_key || '') === 'updates_new') {
      openQuickUpdateModal(w);
      return;
    }
    if (
      String(w.kind || '') === 'shortcut' &&
      String(w.module_key || '') === 'menus' &&
      (String(w.action_key || '') === 'menus_formula_edit' ||
        String(w.action_key || '') === 'menus_daily' ||
        String(w.action_key || '') === 'menus_weekly')
    ) {
      openQuickMenuActionModal(w);
      return;
    }
    if (String(w.kind || '') === 'shortcut' && String(w.module_key || '') === 'projects') {
      if (String(w.route || '').startsWith('/')) onNavigate?.(w.route);
      return;
    }
    if (String(w.kind || '') === 'shortcut') {
      setRuntimeHint(w.id, { metric: 'Action rapide', detail: 'Cette action inline arrive bientôt' });
      return;
    }
    if (canOpen && String(w.route || '').startsWith('/')) {
      onNavigate?.(w.route);
    }
  }

  const resolved = useMemo(() => {
    return (widgets || [])
      .map((raw, idx) => normalizeWidget(raw, idx))
      .map((w) => {
        if (!w) return null;
        if (w.kind === 'link') {
          const route = String(w.route || '').trim();
          if (!route.startsWith('/')) return null;
          return {
            ...w,
            title: defaultWidgetTitle(w),
            color: '#94a3b8',
            route,
            iconKey: 'dashboard',
          };
        }

        const mod = availableByKey.get(String(w.module_key || ''));
        if (!mod) return null;

        const route = resolveWidgetRoute(w, String(mod.route || ''), insights);
        return {
          ...w,
          title: defaultWidgetTitle(w, mod.label),
          color: moduleColor(mod.key, mod.color),
          route,
          iconKey: mod.key,
        };
      })
      .filter(Boolean);
  }, [widgets, availableByKey, insights]);

  function updateWidgetById(id, patch, { animateSize = false } = {}) {
    const wid = String(id || '');
    if (!wid) return;
    let sizeChanged = false;
    setWidgets((prev) =>
      (prev || []).map((w) => {
        if (String(w?.id) !== wid) return w;
        const before = normalizeSize(w?.size);
        const after = normalizeSize((patch || {}).size ?? before);
        if (before !== after) sizeChanged = true;
        return { ...(w || {}), ...(patch || {}) };
      })
    );
    if (animateSize && sizeChanged) {
      setResizingWidgetId(wid);
      if (resizeTimerRef.current) window.clearTimeout(resizeTimerRef.current);
      resizeTimerRef.current = window.setTimeout(() => setResizingWidgetId(''), 760);
    }
  }

  function removeWidgetById(id) {
    const wid = String(id || '');
    if (!wid) return;
    setWidgets((prev) => (prev || []).filter((w) => String(w?.id) !== wid));
  }

  function reorderWidgets(sourceId, targetId, side = 'before') {
    const s = String(sourceId || '');
    const t = String(targetId || '');
    if (!s || !t || s === t) return;
    setWidgets((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      const from = arr.findIndex((w) => String(w?.id) === s);
      const to = arr.findIndex((w) => String(w?.id) === t);
      if (from < 0 || to < 0) return prev;
      const [item] = arr.splice(from, 1);
      let insertAt = to;
      if (from < to) insertAt -= 1;
      if (side === 'after') insertAt += 1;
      insertAt = Math.max(0, Math.min(insertAt, arr.length));
      arr.splice(insertAt, 0, item);
      return arr;
    });
  }

  function onWidgetContextMenu(e, widget) {
    e.preventDefault();
    const w = widget || null;
    if (!w) return;
    const sizes = SIZE_CHOICES.map((s) => ({
      key: `size_${s.key}`,
      label: `${normalizeSize(w.size) === s.key ? '✓ ' : ''}Taille ${s.label.toLowerCase()}`,
      onClick: () => updateWidgetById(w.id, { size: s.key }, { animateSize: true }),
    }));

    setMenu({
      widgetId: String(w.id),
      x: Number(e.clientX) || 0,
      y: Number(e.clientY) || 0,
      items: [
        {
          key: 'open',
          label: 'Ouvrir',
          onClick: () => {
            if (String(w.route || '').startsWith('/')) onNavigate?.(w.route);
          },
          disabled: !String(w.route || '').startsWith('/'),
        },
        {
          key: 'props',
          label: 'Propriétés…',
          onClick: () => {
            setModal({
              type: 'widget-props',
              widgetId: String(w.id),
              form: {
                title: String(w.title || ''),
                size: normalizeSize(w.size),
                page_path: String(w.page_path || ''),
                action_key: String(w.action_key || ''),
                project_id: Number(w.project_id || 0),
                script_id: Number(w.script_id || 0),
                formula_id: Number(w.formula_id || 0),
              },
            });
          },
        },
        { type: 'sep', key: 'sep1' },
        ...sizes,
        { type: 'sep', key: 'sep2' },
        {
          key: 'remove',
          label: 'Supprimer',
          variant: 'danger',
          onClick: () => removeWidgetById(w.id),
        },
      ],
    });
  }

  const byId = useMemo(() => {
    const m = new Map();
    for (const w of widgets || []) {
      if (!w) continue;
      m.set(String(w.id || ''), w);
    }
    return m;
  }, [widgets]);

  return (
    <div className="Page">
      <header className="PageHeader">
        <div className="PageHeaderBar">
          <Breadcrumb items={[{ label: 'Dashboard' }]} />
        </div>
      </header>

      <div className="DashboardWidgetsGrid">
        {resolved.map((w) => {
          const isTarget = String(dragTarget.id) === String(w.id);
          const isDragging = String(dragWidgetId) === String(w.id);
          const dropClass = isTarget ? (dragTarget.side === 'after' ? 'is-drop-after' : 'is-drop-before') : '';
          const sizeClass = `DashboardWidgetWrap--size-${normalizeSize(w.size)}`;
          return (
            <div
              key={w.id}
              className={`DashboardWidgetWrap ${sizeClass} ${dropClass} ${isDragging ? 'is-dragging' : ''} ${String(resizingWidgetId) === String(w.id) ? 'is-resizing' : ''}`}
              draggable
              onDragStart={(e) => {
                setDragWidgetId(String(w.id));
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', String(w.id));
              }}
              onDragEnd={() => {
                setDragWidgetId('');
                setDragTarget({ id: '', side: 'before' });
              }}
              onDragOver={(e) => {
                e.preventDefault();
                if (!dragWidgetId || String(dragWidgetId) === String(w.id)) return;
                const rect = e.currentTarget.getBoundingClientRect();
                const side = e.clientX < rect.left + rect.width / 2 ? 'before' : 'after';
                setDragTarget({ id: String(w.id), side });
              }}
              onDrop={(e) => {
                e.preventDefault();
                const src = String(e.dataTransfer.getData('text/plain') || dragWidgetId || '');
                if (!src) return;
                reorderWidgets(src, w.id, dragTarget.side || 'before');
                setDragWidgetId('');
                setDragTarget({ id: '', side: 'before' });
              }}
              onContextMenu={(e) => onWidgetContextMenu(e, w)}
            >
              <DashboardWidget
                widget={w}
                insights={insights}
                onNavigate={onNavigate}
                resizing={String(resizingWidgetId) === String(w.id)}
                onWidgetClick={handleWidgetClick}
                runtimeHint={runtimeByWidgetId?.[String(w.id)] || null}
              />
            </div>
          );
        })}

        <button type="button" className="DashboardWidget DashboardWidget--add DashboardWidget--size-sm" onClick={() => setModal({ type: 'add-widget' })}>
          <div className="DashboardWidgetHead">
            <span className="DashboardWidgetIcon" aria-hidden="true">
              <IconPlus />
            </span>
            <span className="DashboardWidgetTitle">Ajouter</span>
          </div>
        </button>
      </div>

      <WidgetContextMenu menu={menu} onClose={() => setMenu(null)} />

      {modal?.type === 'add-widget' ? (
        <AddWidgetModal
          availableModules={availableModules}
          menuFormulas={Array.isArray(insights?.menus?.formulas_list) ? insights.menus.formulas_list : []}
          onClose={() => setModal(null)}
          onAdd={(widget) => {
            const draft = { id: newId('w'), ...widget };
            if (!requiresMandatoryProperties(draft)) {
              setWidgets((prev) => [...(prev || []), draft]);
              setModal(null);
              return;
            }
            setModal({
              type: 'widget-props-create',
              draft,
              form: widgetFormFromValue(draft),
              error: '',
            });
          }}
        />
      ) : null}

      {modal?.type === 'widget-props' ? (
        <WidgetPropertiesModal
          widget={byId.get(String(modal.widgetId || '')) || null}
          form={modal.form || {}}
          insights={insights}
          error={String(modal.error || '')}
          onClose={() => setModal(null)}
          onChange={(patch) =>
            setModal((m) =>
              m?.type === 'widget-props'
                ? { ...m, error: '', form: { ...(m.form || {}), ...(patch || {}) } }
                : m
            )
          }
          onSave={() => {
            const current = byId.get(String(modal.widgetId || '')) || null;
            if (!current) {
              setModal(null);
              return;
            }
            const nextPatch = {
              title: String(modal.form?.title || '').trim(),
              size: normalizeSize(modal.form?.size),
            };
            if (String(current.kind) === 'stats' && String(current.metric_key) === 'traffic_page_views') {
              nextPatch.page_path = String(modal.form?.page_path || '').trim();
            }
            if (String(current.kind) === 'shortcut' && String(current.module_key) === 'projects') {
              nextPatch.action_key = String(modal.form?.action_key || current.action_key || 'projects_last');
              nextPatch.project_id = Number(modal.form?.project_id || 0);
            }
            if (String(current.kind) === 'shortcut' && String(current.module_key) === 'scripts') {
              nextPatch.action_key = String(modal.form?.action_key || current.action_key || 'scripts_run_last');
              nextPatch.script_id = Number(modal.form?.script_id || 0);
            }
            if (String(current.kind) === 'shortcut' && String(current.module_key) === 'menus') {
              nextPatch.action_key = String(modal.form?.action_key || current.action_key || 'menus_formula_edit');
              nextPatch.formula_id = Number(modal.form?.formula_id || 0);
            }
            const nextWidget = { ...(current || {}), ...nextPatch };
            const invalid = validateWidgetRequiredFields(nextWidget);
            if (invalid) {
              setModal((m) => (m?.type === 'widget-props' ? { ...m, error: invalid } : m));
              return;
            }
            updateWidgetById(current.id, nextPatch, { animateSize: true });
            setModal(null);
          }}
          onRemove={() => {
            const current = byId.get(String(modal.widgetId || '')) || null;
            if (!current) return;
            removeWidgetById(current.id);
            setModal(null);
          }}
        />
      ) : null}

      {modal?.type === 'widget-props-create' ? (
        <WidgetPropertiesModal
          widget={modal.draft || null}
          form={modal.form || {}}
          insights={insights}
          error={String(modal.error || '')}
          confirmLabel="Créer le widget"
          onClose={() => setModal(null)}
          onChange={(patch) =>
            setModal((m) =>
              m?.type === 'widget-props-create'
                ? { ...m, error: '', form: { ...(m.form || {}), ...(patch || {}) } }
                : m
            )
          }
          onSave={() => {
            const base = modal.draft || null;
            if (!base) {
              setModal(null);
              return;
            }
            const nextWidget = {
              ...(base || {}),
              title: String(modal.form?.title || '').trim(),
              size: normalizeSize(modal.form?.size),
              page_path: String(modal.form?.page_path || '').trim(),
              action_key: String(modal.form?.action_key || base.action_key || ''),
              project_id: Number(modal.form?.project_id || 0),
              script_id: Number(modal.form?.script_id || 0),
              formula_id: Number(modal.form?.formula_id || 0),
            };
            const invalid = validateWidgetRequiredFields(nextWidget);
            if (invalid) {
              setModal((m) => (m?.type === 'widget-props-create' ? { ...m, error: invalid } : m));
              return;
            }
            setWidgets((prev) => [...(prev || []), nextWidget]);
            setModal(null);
          }}
        />
      ) : null}

      {modal?.type === 'quick-update' ? (
        <Modal
          title="Nouvelle mise à jour"
          icon={<IconPlus />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)} disabled={Boolean(modal.saving)}>
                Annuler
              </button>
              <button
                type="button"
                className="Button"
                disabled={Boolean(modal.saving)}
                onClick={async () => {
                  if (modal?.saving) return;
                  const title = String(modal?.title || '').trim();
                  const level = String(modal?.level || 'minor');
                  const changesText = String(modal?.changes_text || '');
                  if (!title) {
                    setModal((m) => (m?.type === 'quick-update' ? { ...m, error: 'Titre requis.' } : m));
                    return;
                  }
                  if (!changesText.trim()) {
                    setModal((m) => (m?.type === 'quick-update' ? { ...m, error: 'Ajoute au moins une modification.' } : m));
                    return;
                  }
                  setModal((m) => (m?.type === 'quick-update' ? { ...m, saving: true, error: '' } : m));
                  const res = await api.updatesCreate({
                    site_id: Number(siteId),
                    title,
                    level,
                    changes_text: changesText,
                    send_email: Boolean(level === 'major' ? true : modal?.send_email),
                  });
                  if (!res?.ok) {
                    setModal((m) =>
                      m?.type === 'quick-update'
                        ? { ...m, saving: false, error: String(res?.error || 'Création impossible') }
                        : m
                    );
                    return;
                  }
                  mergeUpdateIntoInsights(res.update || null);
                  if (modal?.widgetId) {
                    setRuntimeHint(String(modal.widgetId), {
                      metric: 'Créée',
                      detail: String(title),
                    });
                  }
                  setModal(null);
                }}
              >
                {modal.saving ? 'Création…' : 'Créer'}
              </button>
            </>
          }
        >
          <div className="FormGrid">
            <div className="Field">
              <label className="Label">Titre</label>
              <input
                className="Input"
                value={String(modal.title || '')}
                onChange={(e) =>
                  setModal((m) =>
                    m?.type === 'quick-update' ? { ...m, title: e.target.value, error: '' } : m
                  )
                }
                disabled={Boolean(modal.saving)}
              />
            </div>
            <div className="Field">
              <label className="Label">Niveau</label>
              <select
                className="Select"
                value={String(modal.level || 'minor')}
                onChange={(e) =>
                  setModal((m) =>
                    m?.type === 'quick-update'
                      ? {
                          ...m,
                          level: e.target.value,
                          send_email: e.target.value === 'major' ? true : Boolean(m.send_email),
                          error: '',
                        }
                      : m
                  )
                }
                disabled={Boolean(modal.saving)}
              >
                <option value="minor">Mineur</option>
                <option value="major">Majeur</option>
              </select>
            </div>
          </div>
          <div className="Field" style={{ marginTop: 10 }}>
            <label className="Label">Modifications (1 ligne = 1 point)</label>
            <textarea
              className="Textarea"
              rows={8}
              value={String(modal.changes_text || '')}
              onChange={(e) =>
                setModal((m) =>
                  m?.type === 'quick-update' ? { ...m, changes_text: e.target.value, error: '' } : m
                )
              }
              disabled={Boolean(modal.saving)}
              placeholder={'Ex:\nCorrection responsive sur la page d’accueil\nAmélioration du menu mobile'}
            />
          </div>
          <div className="Field" style={{ marginTop: 10 }}>
            <label className="ToggleRow">
              <input
                type="checkbox"
                checked={String(modal.level || '') === 'major' ? true : Boolean(modal.send_email)}
                onChange={(e) =>
                  setModal((m) =>
                    m?.type === 'quick-update'
                      ? { ...m, send_email: Boolean(e.target.checked), error: '' }
                      : m
                  )
                }
                disabled={Boolean(modal.saving) || String(modal.level || '') === 'major'}
              />
              <span>Envoyer un email au contact</span>
            </label>
            {String(modal.level || '') === 'major' ? (
              <div className="Hint">Obligatoire pour une mise à jour majeure.</div>
            ) : null}
          </div>
          {modal.error ? (
            <div className="Error" role="alert" style={{ marginTop: 10 }}>
              {modal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {modal?.type === 'quick-menu-action' ? (
        <Modal
          title="Modifier une formule"
          icon={<IconPlus />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)} disabled={Boolean(modal.saving)}>
                Annuler
              </button>
              <button
                type="button"
                className="Button"
                disabled={Boolean(modal.saving)}
                onClick={async () => {
                  if (modal?.saving) return;
                  const title = String(modal?.title || '').trim();
                  const subtitle = String(modal?.subtitle || '').trim();
                  const description = String(modal?.description || '').trim();
                  const price = String(modal?.price || '').trim();
                  const formulaId = Number(modal?.formula_id || 0);
                  if (!title) {
                    setModal((m) => (m?.type === 'quick-menu-action' ? { ...m, error: 'Nom requis.' } : m));
                    return;
                  }
                  if (!Number.isFinite(formulaId) || formulaId <= 0) {
                    setModal((m) => (m?.type === 'quick-menu-action' ? { ...m, error: 'Formule introuvable.' } : m));
                    return;
                  }
                  setModal((m) => (m?.type === 'quick-menu-action' ? { ...m, saving: true, error: '' } : m));
                  const getRes = await api.menusGet(Number(siteId));
                  if (!getRes?.ok) {
                    setModal((m) =>
                      m?.type === 'quick-menu-action'
                        ? { ...m, saving: false, error: String(getRes?.error || 'Chargement menu impossible') }
                        : m
                    );
                    return;
                  }
                  const safeMenu = safeMenuDataForDashboard(getRes.menu || null);
                  const nextFormulas = (Array.isArray(safeMenu.formulas) ? safeMenu.formulas : []).map((f) =>
                    Number(f?.id || 0) === formulaId
                      ? {
                          ...(f || {}),
                          title,
                          subtitle,
                          description,
                          price,
                          is_available: f?.is_available !== false,
                        }
                      : f
                  );
                  const nextMenu = { ...safeMenu, formulas: nextFormulas };
                  const saveRes = await api.menusSave({ site_id: Number(siteId), menu: nextMenu });
                  if (!saveRes?.ok) {
                    setModal((m) =>
                      m?.type === 'quick-menu-action'
                        ? { ...m, saving: false, error: String(saveRes?.error || 'Enregistrement impossible') }
                        : m
                    );
                    return;
                  }
                  if (modal?.widgetId) {
                    setRuntimeHint(String(modal.widgetId), {
                      metric: 'Carte mise à jour',
                      detail: title,
                    });
                  }
                  setInsights((prev) => {
                    const cur = prev && typeof prev === 'object' ? prev : {};
                    const safe = safeMenuDataForDashboard(saveRes.menu || nextMenu);
                    const cats = Array.isArray(safe?.permanent?.categories) ? safe.permanent.categories : [];
                    const formulas = Array.isArray(safe?.formulas) ? safe.formulas : [];
                    return {
                      ...cur,
                      menus: {
                        categories: cats.length,
                        formulas: formulas.length,
                        formulas_list: formulas
                          .map((f) => ({
                            id: Number(f?.id || 0),
                            title: String(f?.title || ''),
                            subtitle: String(f?.subtitle || ''),
                            description: String(f?.description || ''),
                            price: String(f?.price || ''),
                            is_available: f?.is_available !== false,
                          }))
                          .filter((f) => Number.isFinite(f.id) && f.id > 0),
                      },
                    };
                  });
                  setModal(null);
                }}
              >
                {modal.saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </>
          }
        >
          <div className="FormGrid">
            <div className="Field">
              <label className="Label">Formule</label>
              <input className="Input" value={String(modal.formula_label || '')} disabled />
            </div>
            <div className="Field">
              <label className="Label">Titre</label>
              <input
                className="Input"
                value={String(modal.title || '')}
                onChange={(e) =>
                  setModal((m) =>
                    m?.type === 'quick-menu-action' ? { ...m, title: e.target.value, error: '' } : m
                  )
                }
                disabled={Boolean(modal.saving)}
              />
            </div>
          </div>
          <div className="Field" style={{ marginTop: 10 }}>
            <label className="Label">Description</label>
            <textarea
              className="Textarea"
              rows={5}
              value={String(modal.description || '')}
              onChange={(e) =>
                setModal((m) =>
                  m?.type === 'quick-menu-action' ? { ...m, description: e.target.value, error: '' } : m
                )
              }
              disabled={Boolean(modal.saving)}
            />
          </div>
          <div className="Field" style={{ marginTop: 10 }}>
            <label className="Label">Prix (optionnel)</label>
            <input
              className="Input"
              value={String(modal.price || '')}
              onChange={(e) =>
                setModal((m) =>
                  m?.type === 'quick-menu-action' ? { ...m, price: e.target.value, error: '' } : m
                )
              }
              disabled={Boolean(modal.saving)}
            />
          </div>
          {modal.error ? (
            <div className="Error" role="alert" style={{ marginTop: 10 }}>
              {modal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}
    </div>
  );
}

function AddWidgetModal({ availableModules, menuFormulas, onClose, onAdd }) {
  const [selectedModuleKey, setSelectedModuleKey] = useState(String(availableModules?.[0]?.key || ''));
  const [size, setSize] = useState('md');

  useEffect(() => {
    const first = String(availableModules?.[0]?.key || '');
    if (!selectedModuleKey && first) setSelectedModuleKey(first);
    if (selectedModuleKey && !(availableModules || []).some((m) => String(m.key) === selectedModuleKey)) {
      setSelectedModuleKey(first);
    }
  }, [availableModules, selectedModuleKey]);

  const selectedModule = useMemo(() => {
    return (availableModules || []).find((m) => String(m.key) === String(selectedModuleKey)) || null;
  }, [availableModules, selectedModuleKey]);

  const templates = useMemo(() => {
    if (!selectedModule) return { actions: [], infos: [], graphs: [] };
    return moduleTemplates(String(selectedModule.key || ''), String(selectedModule.label || 'Module'), {
      menuFormulas,
    });
  }, [selectedModule, menuFormulas]);

  function addFromTemplate(tpl) {
    const base = {
      kind: String(tpl.kind || 'module'),
      module_key: String(tpl.module_key || selectedModule?.key || ''),
      title: String(tpl.title || selectedModule?.label || ''),
      size: normalizeSize(size),
    };

    if (base.kind === 'shortcut') {
      onAdd({
        ...base,
        action_key: String(tpl.action_key || ''),
        project_id: Number(tpl.project_id || 0),
        script_id: Number(tpl.script_id || 0),
        formula_id: Number(tpl.formula_id || 0),
      });
      return;
    }
    if (base.kind === 'stats') {
      onAdd({
        ...base,
        metric_key: String(tpl.metric_key || ''),
        page_path: String(tpl.page_path || ''),
      });
      return;
    }
    if (base.kind === 'graph') {
      onAdd({
        ...base,
        metric_key: String(tpl.metric_key || ''),
      });
      return;
    }

    onAdd(base);
  }

  return (
    <Modal
      title="Ajouter un widget"
      icon={<IconPlus />}
      onClose={onClose}
      footer={
        <button type="button" className="Button Button--ghost" onClick={onClose}>
          Fermer
        </button>
      }
    >
      <div className="DashboardAddShell">
        <div className="DashboardAddLayout">
        <div className="DashboardAddModules">
          <div className="Field">
            <label className="Label">Module</label>
            <div className="List DashboardAddScroll" role="list">
              {(availableModules || []).map((m) => {
                const active = String(selectedModuleKey) === String(m.key);
                return (
                  <button
                    key={m.key}
                    type="button"
                    className={`ListItem ListItem--button ${active ? 'is-active' : ''}`}
                    onClick={() => setSelectedModuleKey(String(m.key))}
                    style={{ '--module-accent': moduleColor(m.key, m.color) }}
                  >
                    <div className="UserName">
                      <span className="ButtonInline" style={{ color: moduleColor(m.key, m.color) }}>
                        <ModuleIcon moduleKey={m.key} /> {m.label}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="Field" style={{ marginTop: 8 }}>
            <label className="Label">Taille du widget</label>
            <select className="Select" value={size} onChange={(e) => setSize(normalizeSize(e.target.value))}>
              {SIZE_CHOICES.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="DashboardAddTemplates">
          {!selectedModule ? <div className="Muted">Aucun module disponible.</div> : null}

          {selectedModule ? (
            <>
              <div className="TitleMd" style={{ marginBottom: 8 }}>
                {selectedModule.label}
              </div>

              <TemplateSection title="Actions" list={templates.actions} onAdd={addFromTemplate} />
              <TemplateSection title="Infos" list={templates.infos} onAdd={addFromTemplate} />
              <TemplateSection title="Graphiques" list={templates.graphs} onAdd={addFromTemplate} />
            </>
          ) : null}
        </div>
        </div>
      </div>
    </Modal>
  );
}

function TemplateSection({ title, list, onAdd }) {
  const items = Array.isArray(list) ? list : [];
  return (
    <div style={{ marginTop: 10 }}>
      <div className="LabelStrong" style={{ marginBottom: 8 }}>{title}</div>
      {items.length === 0 ? (
        <div className="Muted" style={{ marginBottom: 8 }}>Aucun élément.</div>
      ) : (
        <div className="DashboardTemplateGrid">
          {items.map((tpl) => (
            <button key={tpl.key} type="button" className="DashboardTemplateCard" onClick={() => onAdd?.(tpl)}>
              <div className="ButtonInline" style={{ gap: 8, color: moduleColor(tpl.module_key) }}>
                <ModuleIcon moduleKey={tpl.module_key} />
                <strong>{tpl.title}</strong>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function WidgetPropertiesModal({
  widget,
  form,
  insights,
  onClose,
  onChange,
  onSave,
  onRemove,
  error = '',
  confirmLabel = 'Enregistrer',
}) {
  if (!widget) return null;
  const isTrafficPageStats = String(widget.kind) === 'stats' && String(widget.metric_key) === 'traffic_page_views';
  const isProjectsShortcut = String(widget.kind) === 'shortcut' && String(widget.module_key) === 'projects';
  const isMenusShortcut = String(widget.kind) === 'shortcut' && String(widget.module_key) === 'menus';
  const isScriptsShortcut = String(widget.kind) === 'shortcut' && String(widget.module_key) === 'scripts';
  const topPages = Array.isArray(insights?.traffic?.topPages) ? insights.traffic.topPages : [];
  const projects = Array.isArray(insights?.projects?.list) ? insights.projects.list : [];
  const menuFormulas = Array.isArray(insights?.menus?.formulas_list) ? insights.menus.formulas_list : [];
  const scripts = Array.isArray(insights?.scripts?.list) ? insights.scripts.list : [];

  return (
    <Modal
      title="Propriétés du widget"
      icon={<IconEdit />}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="Button Button--ghost" onClick={onClose}>
            Annuler
          </button>
          {typeof onRemove === 'function' ? (
            <button type="button" className="Button Button--ghost Button--danger" onClick={onRemove}>
              <span className="ButtonInline">
                <IconTrash /> Supprimer
              </span>
            </button>
          ) : null}
          <button type="button" className="Button" onClick={onSave}>
            {confirmLabel}
          </button>
        </>
      }
    >
      <div className="FormGrid">
        <div className="Field">
          <label className="Label">Titre</label>
          <input className="Input" value={String(form?.title || '')} onChange={(e) => onChange?.({ title: e.target.value })} />
        </div>
        <div className="Field">
          <label className="Label">Taille</label>
          <select className="Select" value={normalizeSize(form?.size)} onChange={(e) => onChange?.({ size: normalizeSize(e.target.value) })}>
            {SIZE_CHOICES.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      {isTrafficPageStats ? (
        <div className="Field" style={{ marginTop: 10 }}>
          <label className="Label">Page suivie</label>
          <select className="Select" value={String(form?.page_path || '')} onChange={(e) => onChange?.({ page_path: e.target.value })}>
            <option value="">Choisir une page…</option>
            {topPages.map((p) => (
              <option key={p.path} value={p.path}>{p.path}</option>
            ))}
          </select>
          <div className="Hint">Tu peux afficher les vues d’une page précise dans ce widget.</div>
        </div>
      ) : null}

      {isProjectsShortcut ? (
        <div className="FormGrid" style={{ marginTop: 10 }}>
          <div className="Field">
            <label className="Label">Action</label>
            <select className="Select" value={String(form?.action_key || 'projects_last')} onChange={(e) => onChange?.({ action_key: e.target.value })}>
              <option value="projects_last">Modifier le dernier projet</option>
              <option value="projects_open_id">Modifier un projet précis</option>
            </select>
          </div>
          {String(form?.action_key || '') === 'projects_open_id' ? (
            <div className="Field">
              <label className="Label">Projet</label>
              <select className="Select" value={String(Number(form?.project_id || 0) || '')} onChange={(e) => onChange?.({ project_id: Number(e.target.value || 0) })}>
                <option value="">Choisir…</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name || `Projet #${p.id}`}</option>
                ))}
              </select>
            </div>
          ) : null}
        </div>
      ) : null}

      {isMenusShortcut ? (
        <div className="FormGrid" style={{ marginTop: 10 }}>
          <div className="Field">
            <label className="Label">Action</label>
            <select className="Select" value={String(form?.action_key || 'menus_formula_edit')} onChange={(e) => onChange?.({ action_key: e.target.value })}>
              <option value="menus_formula_edit">Modifier une formule</option>
            </select>
          </div>
          <div className="Field">
            <label className="Label">Formule</label>
            <select className="Select" value={String(Number(form?.formula_id || 0) || '')} onChange={(e) => onChange?.({ formula_id: Number(e.target.value || 0) })}>
              <option value="">Choisir…</option>
              {menuFormulas.map((f) => (
                <option key={f.id} value={f.id}>{menuFormulaDisplayName(f)}</option>
              ))}
            </select>
          </div>
        </div>
      ) : null}

      {isScriptsShortcut ? (
        <div className="FormGrid" style={{ marginTop: 10 }}>
          <div className="Field">
            <label className="Label">Action</label>
            <select className="Select" value={String(form?.action_key || 'scripts_run_last')} onChange={(e) => onChange?.({ action_key: e.target.value })}>
              <option value="scripts_run_last">Exécuter le dernier script</option>
              <option value="scripts_run_id">Exécuter un script précis</option>
            </select>
          </div>
          {String(form?.action_key || '') === 'scripts_run_id' ? (
            <div className="Field">
              <label className="Label">Script</label>
              <select className="Select" value={String(Number(form?.script_id || 0) || '')} onChange={(e) => onChange?.({ script_id: Number(e.target.value || 0) })}>
                <option value="">Choisir…</option>
                {scripts.map((s) => (
                  <option key={s.id} value={s.id}>{s.name || `Script #${s.id}`}</option>
                ))}
              </select>
            </div>
          ) : null}
        </div>
      ) : null}

      {error ? (
        <div className="Error" role="alert" style={{ marginTop: 10 }}>
          {error}
        </div>
      ) : null}
    </Modal>
  );
}
